/** Role-filtered navigation model (PL-03 / ST-01 / ML-01). Icons = lucide. */
import type { AppRole } from '@gym/shared';
import {
  Building2,
  CalendarDays,
  LayoutDashboard,
  LineChart,
  Settings,
  Store,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

export type NavGroup = 'operate' | 'manage' | 'system';

export interface NavItem {
  labelKey: string;
  path: string;
  icon: LucideIcon;
  allowedRoles: AppRole[];
  group: NavGroup;
}

const ALL: AppRole[] = ['owner', 'manager', 'front_desk', 'trainer'];
const DESK: AppRole[] = ['owner', 'manager', 'front_desk'];
const MGMT: AppRole[] = ['owner', 'manager'];
const OWNER: AppRole[] = ['owner'];

export const NAV_ITEMS: NavItem[] = [
  // The classic Dashboard is the home/landing (/dashboard) and is first in the nav.
  {
    labelKey: 'nav.dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    allowedRoles: ALL,
    group: 'operate',
  },
  { labelKey: 'nav.members', path: '/members', icon: Users, allowedRoles: DESK, group: 'operate' },
  {
    labelKey: 'nav.memberships',
    path: '/memberships',
    icon: Wallet,
    allowedRoles: DESK,
    group: 'operate',
  },
  // Billing is folded into the Members → Payments tab; the /billing route is kept
  // (B2B invoices) but intentionally unwired from the sidebar (like /dashboard).
  { labelKey: 'nav.retail', path: '/retail', icon: Store, allowedRoles: DESK, group: 'operate' },
  {
    labelKey: 'nav.classes',
    path: '/classes',
    icon: CalendarDays,
    allowedRoles: ALL,
    group: 'operate',
  },
  { labelKey: 'nav.staff', path: '/staff', icon: UserCog, allowedRoles: MGMT, group: 'operate' },
  {
    labelKey: 'nav.analytics',
    path: '/analytics',
    icon: LineChart,
    allowedRoles: MGMT,
    group: 'manage',
  },
  {
    labelKey: 'nav.branches',
    path: '/branches',
    icon: Building2,
    allowedRoles: OWNER,
    group: 'manage',
  },
  {
    labelKey: 'nav.settings',
    path: '/settings',
    icon: Settings,
    allowedRoles: OWNER,
    group: 'system',
  },
];

export function navFor(role: AppRole | undefined): NavItem[] {
  if (!role) return [];
  return NAV_ITEMS.filter((i) => i.allowedRoles.includes(role));
}
